/**
 * Minimap — absolute-positioned vertical navigation strip showing every
 * change in `display_order` (M3 Phase 3.4 / `comparison-visual-language.md`
 * §4).
 *
 * Layout (two modes, persisted to localStorage when uncontrolled):
 *
 *   - compact (default): blocks flex-1 vertically so the strip fills 80vh
 *     evenly regardless of where in the document the changes live.
 *   - proportional: blocks are absolutely positioned at
 *     `top: displayOrder / (maxDisplayOrder + 1)` of the strip height. Gives
 *     the reviewer a real sense of "front-loaded" vs "back-loaded" diffs by
 *     letting unchanged stretches show up as whitespace.
 *
 * Both modes share the same block width — no index labels, no per-mode
 * width difference. The strip is intentionally narrow (24px) so it occupies
 * minimal horizontal real estate in the host's layout.
 *
 * Mode control: by default the workspace owns the mode (persisted to
 * localStorage) and renders a ModeToggle on top of the strip. A host that
 * wants to place the toggle elsewhere (e.g. partner-jerry's WorkspaceTopBar)
 * passes controlled `mode` + `onModeChange` props — the internal toggle is
 * then suppressed.
 *
 * Cursors layered on each block (must remain visually independent):
 *
 *   - 2px blue stripe on the left edge → `selectedChangeId` (keyboard /
 *     click focus, the change the reviewer is operating on).
 *   - Outer ring → `visibleChangeIds` (viewport intersection — what the
 *     reader currently sees in their scroll window). Wider in compact
 *     mode where rows are tall, thinner in proportional mode where rows
 *     are 4-6px so the ring stays legible.
 *   - Right-edge stroke → accepted decision.
 *   - opacity-50 → rejected decision.
 *
 * Interaction:
 *   - Click on a block → `navigation.select(changeId)`. The hook scrolls
 *     the corresponding paragraph into the viewport centre.
 *   - Hover → tooltip floats to the *left* of the strip.
 *
 * Accessibility:
 *   - Role `navigation` + `aria-label`. Each block is a `<button>` so
 *     keyboard / screen reader users can tab through.
 *   - Colour is reinforced by the change-type label + index inside the
 *     tooltip, i.e. the minimap doesn't rely on colour alone for type
 *     discrimination.
 *
 * Performance:
 *   - 48 changes max in the M1 corpus; flat-list re-render is fine.
 *   - `visibleChangeIds` updates batched by the IntersectionObserver inside
 *     `useChangeNavigation`, so scroll induces ~1 minimap render per tick.
 */
import { memo, useContext, useEffect, useMemo, useState } from 'react';

import type { UseChangeNavigationResult } from './useChangeNavigation';
import { VisibleChangesContext } from './VisibleChangesContext';
import type { DecisionAdapter } from './decisionAdapter';
import type { ChangeResponse, ChangeType, DecisionStatus } from '../../lib/veridoc-api';

interface MinimapProps {
  changes: ChangeResponse[];
  navigation: UseChangeNavigationResult;
  decisions?: DecisionAdapter;
  /**
   * Controlled mode. When passed together with `onModeChange`, the workspace
   * relinquishes mode ownership — no internal ModeToggle is rendered and the
   * host is responsible for persisting / surfacing the choice (e.g. in its
   * own top bar). Omit both to keep the legacy uncontrolled behaviour.
   */
  mode?: MinimapMode;
  onModeChange?: (mode: MinimapMode) => void;
}

export type MinimapMode = 'compact' | 'proportional';
const MINIMAP_MODE_STORAGE_KEY = 'veridoc:comparison:minimapMode';

function loadMinimapMode(): MinimapMode {
  if (typeof window === 'undefined') return 'compact';
  const stored = window.localStorage.getItem(MINIMAP_MODE_STORAGE_KEY);
  return stored === 'proportional' ? 'proportional' : 'compact';
}

function saveMinimapMode(mode: MinimapMode): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(MINIMAP_MODE_STORAGE_KEY, mode);
}

const CHANGE_TYPE_LABEL: Record<ChangeType, string> = {
  addition: '新增',
  deletion: '删除',
  modification: '修改',
  move: '移动',
  format: '格式',
};

const DECISION_STATUS_LABEL: Record<DecisionStatus, string> = {
  pending: '待决定',
  accepted: '已接受',
  rejected: '已拒绝',
};

const TYPE_BAR_CLASS: Record<ChangeType, string> = {
  addition: 'bg-change-addition-bar',
  deletion: 'bg-change-deletion-bar',
  modification: 'bg-change-modification-bar',
  move: 'bg-change-move-bar',
  format: 'bg-change-format-bar',
};

export function Minimap({
  changes,
  navigation,
  decisions,
  mode: controlledMode,
  onModeChange,
}: MinimapProps): React.JSX.Element | null {
  const ordered = useMemo(
    () => [...changes].sort((a, b) => a.displayOrder - b.displayOrder),
    [changes],
  );

  const isControlled = controlledMode !== undefined && onModeChange !== undefined;
  const [internalMode, setInternalMode] = useState<MinimapMode>(() =>
    loadMinimapMode(),
  );
  useEffect(() => {
    if (!isControlled) saveMinimapMode(internalMode);
  }, [internalMode, isControlled]);

  const mode = isControlled ? controlledMode! : internalMode;
  const setMode = isControlled ? onModeChange! : setInternalMode;

  // Degeneracy guard: proportional mode needs distinct displayOrder values
  // to actually spread blocks out. If every value collapses to the same
  // anchor, fall back to compact for this render so blocks don't stack.
  const proportionalUsable = useMemo(() => {
    if (ordered.length < 2) return false;
    const first = ordered[0]!.displayOrder;
    return ordered.some((c) => c.displayOrder !== first);
  }, [ordered]);
  const effectiveMode: MinimapMode =
    mode === 'proportional' && proportionalUsable ? 'proportional' : 'compact';

  const maxDisplayOrder = ordered.length
    ? ordered[ordered.length - 1]!.displayOrder
    : 0;

  if (ordered.length === 0) return null;

  return (
    <nav
      aria-label="变更导航条"
      className="absolute right-4 top-1/2 z-dropdown hidden -translate-y-1/2 lg:flex lg:flex-col lg:items-end lg:gap-2"
    >
      {!isControlled && <ModeToggle value={mode} onChange={setMode} />}
      <ol
        className={[
          'list-none relative w-6 rounded-md border border-ink-200 bg-ink-0/90 p-1 shadow-sm backdrop-blur',
          effectiveMode === 'compact' ? 'flex flex-col gap-1' : 'block',
        ].join(' ')}
        style={{ height: '80vh' }}
      >
        {ordered.map((change, idx) => {
          const total = ordered.length;
          const isSelected = navigation.selectedChangeId === change.id;
          const status = decisions?.getStatus(change.id) ?? 'pending';
          return (
            <MinimapBlock
              key={change.id}
              change={change}
              indexLabel={idx + 1}
              total={total}
              isSelected={isSelected}
              status={status}
              mode={effectiveMode}
              maxDisplayOrder={maxDisplayOrder}
              onSelect={() => navigation.select(change.id)}
            />
          );
        })}
      </ol>
    </nav>
  );
}

interface MinimapBlockProps {
  change: ChangeResponse;
  indexLabel: number;
  total: number;
  isSelected: boolean;
  status: DecisionStatus;
  mode: MinimapMode;
  maxDisplayOrder: number;
  onSelect: () => void;
}

const MinimapBlock = memo(function MinimapBlock({
  change,
  indexLabel,
  total,
  isSelected,
  status,
  mode,
  maxDisplayOrder,
  onSelect,
}: MinimapBlockProps): React.JSX.Element {
  const visibleChangeIds = useContext(VisibleChangesContext);
  const isVisible = visibleChangeIds.has(change.id);
  const typeBar = TYPE_BAR_CLASS[change.changeType];
  const opacity = status === 'rejected' ? 'opacity-50' : 'opacity-100';
  const acceptedRingClass =
    status === 'accepted'
      ? 'after:absolute after:right-0 after:top-0 after:bottom-0 after:w-0.5 after:bg-semantic-success after:content-[""]'
      : '';
  const cursor = isSelected ? 'before:bg-brand-500' : 'before:bg-transparent';
  const visibleRingClass = isVisible
    ? mode === 'compact'
      ? 'ring-2 ring-brand-200'
      : 'ring-1 ring-brand-200'
    : '';

  const typeLabel = CHANGE_TYPE_LABEL[change.changeType];
  const statusLabel = DECISION_STATUS_LABEL[status];
  const tooltipText = `${typeLabel} · 第 ${indexLabel} / ${total} 处 · ${statusLabel}`;

  // Layout container differs per mode:
  //   compact      → flex-1, fills the strip evenly (legacy)
  //   proportional → absolute, top derived from displayOrder ratio
  // Both modes share the same block width — no index labels.
  const containerClass =
    mode === 'compact'
      ? 'group/block list-none relative min-h-1 flex-1'
      : 'group/block list-none absolute left-1 right-1 h-2';
  const containerStyle: React.CSSProperties | undefined =
    mode === 'proportional'
      ? {
          top: `${Math.min(
            98,
            (change.displayOrder / (maxDisplayOrder + 1)) * 100,
          )}%`,
        }
      : undefined;

  return (
    <li className={containerClass} style={containerStyle}>
      <button
        type="button"
        onClick={onSelect}
        aria-label={tooltipText}
        aria-current={isSelected ? 'true' : undefined}
        className={[
          'relative block h-full w-full rounded-sm transition-opacity duration-fast',
          'before:absolute before:left-0 before:top-0 before:bottom-0 before:w-0.5 before:rounded-sm before:transition-colors before:duration-fast before:content-[""]',
          acceptedRingClass,
          cursor,
          typeBar,
          opacity,
          visibleRingClass,
          'hover:ring-1 hover:ring-ink-400',
        ]
          .filter(Boolean)
          .join(' ')}
      />
      <span
        role="tooltip"
        className={[
          'pointer-events-none absolute right-full top-1/2 mr-2 -translate-y-1/2',
          'whitespace-nowrap rounded-sm bg-ink-900 px-2 py-1 text-xs text-ink-0 shadow-md',
          'opacity-0 transition-opacity duration-fast',
          'group-hover/block:opacity-100 group-focus-within/block:opacity-100',
        ].join(' ')}
      >
        {tooltipText}
      </span>
    </li>
  );
});

// Internal toggle — only rendered when the host lets the workspace own the
// mode (uncontrolled). Hosts that pass `mode` + `onModeChange` render their
// own toggle (e.g. partner-jerry's WorkspaceTopBar).
function ModeToggle({
  value,
  onChange,
}: {
  value: MinimapMode;
  onChange: (mode: MinimapMode) => void;
}): React.JSX.Element {
  return (
    <div
      role="tablist"
      aria-label="缩略图模式"
      className="inline-flex h-btn-sm items-center rounded-md border border-ink-200 bg-ink-50 p-0.5 text-xs shadow-sm"
    >
      <ModeToggleButton
        active={value === 'compact'}
        onClick={() => onChange('compact')}
        label="紧凑"
        title="紧凑模式：所有变更等距铺满，显示序号"
      />
      <ModeToggleButton
        active={value === 'proportional'}
        onClick={() => onChange('proportional')}
        label="比例"
        title="比例模式：按变更在文档中的位置分布，不显示序号"
      />
    </div>
  );
}

function ModeToggleButton({
  active,
  onClick,
  label,
  title,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  title: string;
}): React.JSX.Element {
  const base =
    'inline-flex h-6 items-center rounded px-2 text-xs font-medium transition-colors duration-fast';
  const tone = active
    ? 'bg-ink-0 text-ink-900 shadow-sm'
    : 'text-ink-500 hover:text-ink-700';
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      title={title}
      className={`${base} ${tone}`}
    >
      {label}
    </button>
  );
}

export default Minimap;
